import { useState } from "react";
import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { useSwitchCompany } from "@/components/layout/CompanySwitcher";
import type { ApiResponse, CompanyWithRole } from "@/types";

interface CreateCompanyModalProps {
  opened: boolean;
  onClose: () => void;
}

export function CreateCompanyModal({ opened, onClose }: CreateCompanyModalProps) {
  const [name, setName] = useState("");
  const loadUser = useAuthStore((s) => s.loadUser);
  const switchCompany = useSwitchCompany();

  const createCompany = useMutation({
    mutationFn: (companyName: string) =>
      api.post<ApiResponse<CompanyWithRole>>("/companies", { name: companyName }),
    onSuccess: async (res) => {
      // Refresh memberships BEFORE selecting the new company so stale-id
      // validation in useProperty doesn't bounce the selection back.
      await loadUser();
      switchCompany(res.data.id);
      notifications.show({
        title: "Company created",
        message: `You are now the owner of ${res.data.name}.`,
        color: "green",
      });
      setName("");
      onClose();
    },
    onError: (error) => {
      const message =
        error instanceof ApiError ? error.message : "Failed to create company";
      notifications.show({ title: "Error", message, color: "red" });
    },
  });

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createCompany.mutate(trimmed);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Create company" centered>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          You will be the owner of the new company. Team members can be invited
          from Settings once it's created.
        </Text>
        <TextInput
          label="Company name"
          placeholder="Acme Inc."
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          data-autofocus
          maxLength={255}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={createCompany.isPending}
            disabled={!name.trim()}
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
